import { forEach, get, map, setWith, toPath } from 'lodash';

import {
    ADD_RETARGETING_CONDITION_GROUP,
    ADD_RETARGETING_CONDITION_RULE,
    CHANGE_RETARGETING_CONDITION_EDITOR_IS_SAVING_AS_NEW_CONDITION_DATA,
    CHANGE_RETARGETING_CONDITION_EDITOR_IS_SAVING_DATA,
    CLOSE_RETARGETING_CONDITION_EDITOR,
    COMPLETE_LOADING_RETARGETING_CONDITION_EDITOR_DATA,
    DELETE_RETARGETING_CONDITION_GROUP,
    DELETE_RETARGETING_CONDITION_RULE,
    OPEN_AUDIENCE_RETARGETING_CONDITION_EDITOR,
    OPEN_NEW_AUDIENCE_RETARGETING_CONDITION_EDITOR,
    OPEN_NEW_RETARGETING_CONDITION_EDITOR,
    OPEN_RETARGETING_CONDITION_EDITOR,
    RESET_VISITORS_FORECAST,
    RetargetingConditionEditorAction,
    SAVE_AUDIENCE_RETARGETING_CONDITION,
    SAVE_RETARGETING_CONDITION,
    SAVE_RETARGETING_CONDITION_AS_NEW,
    SAVE_RETARGETING_CONDITION_AS_NEW_SUCCESSED,
    SAVE_RETARGETING_CONDITION_SUCCESSED,
    SHOW_LOADING_RETARGETING_CONDITION_EDITOR_DATA_ERROR,
    SHOW_RETARGETING_CONDITION_EDITOR_SERVER_VALIDATION_ERRORS,
    SHOW_SAVING_RETARGETING_CONDITION_EDITOR_SERVER_GLOBAL_ERROR,
    SHOW_VISITORS_FORECAST_ERROR,
    UPDATE_RETARGETING_CONDITION_DESCRIPTION,
    UPDATE_RETARGETING_CONDITION_EDITOR_GOALS,
    UPDATE_RETARGETING_CONDITION_GROUP_TYPE,
    UPDATE_RETARGETING_CONDITION_NAME,
    UPDATE_RETARGETING_CONDITION_RULE_GOAL,
    UPDATE_RETARGETING_CONDITION_RULE_PERIOD,
    UPDATE_RETARGETING_CONDITION_RULE_TYPE,
    UPDATE_VISITORS_FORECAST
} from '../actions/retargetingConditionEditor';
import { convertServerPathToClient } from '../service/ErrorsMapper/ErrorsMapper';
import {
    getSegmentOrGoalError,
    retargetingConditionServerErrorMapper,
    retargetingConditionServerErrorsTextMapper,
    validateConditionsGroups,
    validateRetargetingConditionDescription,
    validateRetargetingConditionName,
    validateRetargetingConditionRuleGoal,
    validateRetargetingConditionRulePeriod
} from '../service/RetargetingConditionEditorHelpers/RetargetingConditionEditorHelpers';
import { ValidationErrorData, ValidationResultItem } from '../typings/common';
import { CampaignType } from '../typings/domain';
import { GdRetargetingConditionRuleType } from '../typings/graphql-grid';
import {
    RetargetingConditionData,
    RetargetingConditionDataRulesGroup,
    RetargetingConditionEditorDataRule,
    RetargetingConditionEditorState,
    RetargetingConditionRuleType
} from '../typings/retargeting';
import { State } from '../typings/state';

const PERIOD_MAX_VALUE = 540;

export const getEmptyRetargetingConditionData = (): RetargetingConditionData => {
    return {
        id: 'newId',
        name: '',
        hasUnavailableGoals: false,
        isForRatesAdjustment: false,
        hasGeoSegment: false,
        rulesGroups: getEmptyRulesGroups()
    };
};

export const getEmptyRule = (): RetargetingConditionEditorDataRule => {
    const emptyRuleValue = {
        type: RetargetingConditionRuleType.METRIKA_GOAL_RULE,
        id: undefined,
        period: PERIOD_MAX_VALUE,
        isPeriodPristine: true,
        isIdPristine: true,
        errors: {}
    };

    return {
        ...emptyRuleValue
    };
};

export const getEmptyRulesGroups = (): Array<RetargetingConditionDataRulesGroup> => {
    return [
        {
            type: GdRetargetingConditionRuleType.OR,
            items: [getEmptyRule()]
        }
    ];
};

export const getChangedRulesFields = (
    state: RetargetingConditionEditorState
): Pick<
    RetargetingConditionEditorState,
    'wasChanged' | 'isSavingAsNewConditionDisabled' | 'errors' | 'enableVisitorsForecast'
> => {
    const generalErrors = validateConditionsGroups(state.conditionData.rulesGroups, state.campaignType);

    const enableVisitorsForecast = state.campaignType !== CampaignType.CPM_BANNER && generalErrors.length === 0;

    return {
        wasChanged: true,
        isSavingAsNewConditionDisabled: false,
        enableVisitorsForecast: enableVisitorsForecast,
        errors: {
            ...state.errors,
            general: generalErrors
        }
    };
};

export const defaultRetargetingEditorState: RetargetingConditionEditorState = {
    groupId: '', // TODO
    constants: {
        maxNameLength: 250,
        maxDescriptionName: 1000,
        maxRetargetingConditionRulesGroups: 50,
        maxRetargetingConditionRulesInGroup: 250,
        rulePeriodMaxValue: PERIOD_MAX_VALUE
    },
    conditionData: getEmptyRetargetingConditionData(),
    goals: [],
    isVisible: false,
    isNew: false,
    isSaving: false,
    isSavingAsNewCondition: false,
    isSavingAsNewConditionDisabled: true,
    isDataLoading: false,
    enableVisitorsForecast: true,
    errors: {},
    wasChanged: false,
    isNamePristine: true,
    wasSaved: false
};

export function retargetingConditionEditor(
    state: RetargetingConditionEditorState = defaultRetargetingEditorState,
    action: RetargetingConditionEditorAction,
    fullState: State
): RetargetingConditionEditorState {
    switch (action.type) {
        case OPEN_NEW_RETARGETING_CONDITION_EDITOR: {
            return {
                ...state,
                groupId: action.groupId,
                conditionData: getEmptyRetargetingConditionData(),
                isNew: true,
                isVisible: true,
                isDataLoading: true,
                wasChanged: false
            };
        }

        case OPEN_NEW_AUDIENCE_RETARGETING_CONDITION_EDITOR: {
            return {
                ...state,
                groupId: action.groupId,
                conditionData: getEmptyRetargetingConditionData(),
                isNew: true,
                isVisible: true,
                isDataLoading: true,
                wasChanged: false,
                campaignType: CampaignType.CPM_BANNER
            };
        }

        case OPEN_RETARGETING_CONDITION_EDITOR: {
            return {
                ...state,
                groupId: action.groupId,
                conditionData: action.conditionData,
                isVisible: true,
                isDataLoading: true,
                wasChanged: false
            };
        }

        case OPEN_AUDIENCE_RETARGETING_CONDITION_EDITOR: {
            return {
                ...state,
                groupId: action.groupId,
                conditionData: action.conditionData,
                isVisible: true,
                isDataLoading: true,
                wasChanged: false,
                campaignType: CampaignType.CPM_BANNER
            };
        }

        case SHOW_LOADING_RETARGETING_CONDITION_EDITOR_DATA_ERROR: {
            return {
                ...state,
                errors: {
                    dataLoading: [{ text: action.error }]
                }
            };
        }

        case COMPLETE_LOADING_RETARGETING_CONDITION_EDITOR_DATA: {
            return {
                ...state,
                isDataLoading: false
            };
        }

        case CLOSE_RETARGETING_CONDITION_EDITOR: {
            return defaultRetargetingEditorState;
        }

        case UPDATE_RETARGETING_CONDITION_EDITOR_GOALS: {
            return {
                ...state,
                goals: action.goals,
                errors: {}
            };
        }

        case UPDATE_RETARGETING_CONDITION_NAME: {
            return {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    name: action.name
                },
                wasChanged: true,
                isNamePristine: false,
                errors: {
                    ...state.errors,
                    goalsOrSegment: [],
                    name: validateRetargetingConditionName({ maxLength: state.constants.maxNameLength }, action.name)
                }
            };
        }

        case UPDATE_RETARGETING_CONDITION_DESCRIPTION: {
            return {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    description: action.description
                },
                wasChanged: true,
                errors: {
                    ...state.errors,
                    goalsOrSegment: [],
                    description: validateRetargetingConditionDescription(
                        { maxLength: state.constants.maxNameLength },
                        action.description
                    )
                }
            };
        }

        case DELETE_RETARGETING_CONDITION_GROUP: {
            const newRules = [...state.conditionData.rulesGroups];

            newRules.splice(action.groupIndex, 1);

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case ADD_RETARGETING_CONDITION_GROUP: {
            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: [...state.conditionData.rulesGroups, ...getEmptyRulesGroups()]
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case UPDATE_RETARGETING_CONDITION_GROUP_TYPE: {
            const newRules = [...state.conditionData.rulesGroups];
            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                type: action.value
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case UPDATE_RETARGETING_CONDITION_RULE_PERIOD: {
            const newRules = [...state.conditionData.rulesGroups];
            const newRulesItems = [...newRules[action.groupIndex].items];
            const { errors, ...oldValue } = newRulesItems[action.ruleIndex];
            const newValue = {
                ...oldValue,
                period: action.value,
                isPeriodPristine: false
            };

            newRulesItems[action.ruleIndex] = {
                ...newValue,
                errors: {
                    ...errors,
                    period: validateRetargetingConditionRulePeriod(
                        { periodMaxValue: state.constants.rulePeriodMaxValue },
                        newValue
                    )
                }
            };

            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                items: newRulesItems
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case UPDATE_RETARGETING_CONDITION_RULE_GOAL: {
            const newRules = [...state.conditionData.rulesGroups];
            const newRulesItems = [...newRules[action.groupIndex].items];
            const { errors, ...oldValue } = newRulesItems[action.ruleIndex];
            const id = action.value;

            const newValue = {
                ...oldValue,
                id,
                isIdPristine: false
            };

            newRulesItems[action.ruleIndex] = {
                ...newValue,
                errors: {
                    ...errors,
                    id: validateRetargetingConditionRuleGoal(newValue)
                }
            };

            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                items: newRulesItems
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case UPDATE_RETARGETING_CONDITION_RULE_TYPE: {
            const newRules = [...state.conditionData.rulesGroups];
            const newRulesItems = [...newRules[action.groupIndex].items];
            const { errors, ...oldValue } = newRulesItems[action.ruleIndex];
            const newValue = {
                ...oldValue,
                type: action.value,
                id: undefined,
                isIdPristine: true
            };

            newRulesItems[action.ruleIndex] = {
                ...newValue,
                errors: {
                    ...errors,
                    id: validateRetargetingConditionRuleGoal(newValue)
                }
            };

            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                items: newRulesItems
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case DELETE_RETARGETING_CONDITION_RULE: {
            const newRules = [...state.conditionData.rulesGroups];
            const newRulesItems = [...newRules[action.groupIndex].items];
            newRulesItems.splice(action.ruleIndex, 1);

            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                items: newRulesItems
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case ADD_RETARGETING_CONDITION_RULE: {
            const newRules = [...state.conditionData.rulesGroups];

            newRules[action.groupIndex] = {
                ...newRules[action.groupIndex],
                items: [...newRules[action.groupIndex].items, getEmptyRule()]
            };

            const newState = {
                ...state,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRules
                },
                errors: {}
            };

            return {
                ...newState,
                ...getChangedRulesFields(newState)
            };
        }

        case UPDATE_VISITORS_FORECAST: {
            const { visitorsForecast, ...otherErrors } = state.errors;

            return {
                ...state,
                visitorsForecast: action.value,
                errors: otherErrors
            };
        }

        case RESET_VISITORS_FORECAST: {
            const { visitorsForecast, ...otherErrors } = state.errors;

            return {
                ...state,
                visitorsForecast: undefined,
                errors: otherErrors
            };
        }

        case SHOW_VISITORS_FORECAST_ERROR: {
            return {
                ...state,
                visitorsForecast: undefined,
                errors: {
                    ...state.errors,
                    visitorsForecast: [{ text: action.error }]
                }
            };
        }

        case CHANGE_RETARGETING_CONDITION_EDITOR_IS_SAVING_DATA: {
            return {
                ...state,
                isSaving: action.isSaving
            };
        }

        case CHANGE_RETARGETING_CONDITION_EDITOR_IS_SAVING_AS_NEW_CONDITION_DATA: {
            return {
                ...state,
                isSavingAsNewCondition: action.isSavingAsNewCondition
            };
        }

        case SAVE_RETARGETING_CONDITION: {
            return {
                ...validateRetargetingConditionEditorData(state),
                wasSaved: true,
                isSaving: true
            };
        }

        case SAVE_AUDIENCE_RETARGETING_CONDITION: {
            return {
                ...validateAudienceRetargetingConditionEditorData(state),
                wasSaved: true,
                isSaving: true
            };
        }

        case SAVE_RETARGETING_CONDITION_AS_NEW: {
            return {
                ...validateRetargetingConditionEditorData(state),
                wasSaved: true,
                isSavingAsNewCondition: true
            };
        }

        case SAVE_RETARGETING_CONDITION_SUCCESSED: {
            return defaultRetargetingEditorState;
        }

        case SAVE_RETARGETING_CONDITION_AS_NEW_SUCCESSED: {
            return defaultRetargetingEditorState;
        }

        case SHOW_SAVING_RETARGETING_CONDITION_EDITOR_SERVER_GLOBAL_ERROR: {
            return {
                ...state,
                isSaving: false,
                isSavingAsNewCondition: false,
                errors: {
                    ...state.errors,
                    serverGlobal: [{ text: action.error }]
                }
            };
        }

        case SHOW_RETARGETING_CONDITION_EDITOR_SERVER_VALIDATION_ERRORS: {
            const errorsData = convertServerPathToClient(action.errors, retargetingConditionServerErrorMapper).map(
                (error: ValidationResultItem) => {
                    return {
                        path: error.path,
                        code: error.code,
                        text: retargetingConditionServerErrorsTextMapper(error)
                    };
                }
            );
            const errorsTree = getErrorsTree(errorsData);
            const { rulesGroupsGeneral, rulesGroups, name = [], description = [], general = [] } = get(
                errorsTree,
                'retargetingConditions[0]',
                {}
            );
            const newRulesGroups = [...state.conditionData.rulesGroups];

            if (rulesGroups) {
                forEach(rulesGroups, (rulesGroup, rulesGroupIndex) => {
                    if (rulesGroup.items) {
                        forEach(rulesGroup.items, (value, rulesGroupItemIndex) => {
                            const itemWithError = newRulesGroups[rulesGroupIndex].items[rulesGroupItemIndex];

                            itemWithError.errors = value;
                            itemWithError.isIdPristine = false;
                            itemWithError.isPeriodPristine = false;
                        });
                    }
                });
            }

            return {
                ...state,
                isSaving: false,
                isSavingAsNewCondition: false,
                conditionData: {
                    ...state.conditionData,
                    rulesGroups: newRulesGroups
                },
                errors: {
                    visitorsForecast: state.errors.visitorsForecast,
                    name,
                    description,
                    general: [...general, ...(rulesGroupsGeneral || [])]
                }
            };
        }

        default:
            return state;
    }
}

const getErrorsTree = (errorsData: ValidationErrorData[]): object => {
    return errorsData.reduce((acc, next) => {
        const clientPath = toPath(next.path);
        const fieldErrors = get(acc, clientPath);

        if (!fieldErrors) {
            setWith(acc, clientPath, [next], Object);
        } else {
            fieldErrors.push(next);
        }
        return acc;
    }, {});
};

const validateRetargetingConditionEditorData = (
    state: RetargetingConditionEditorState
): RetargetingConditionEditorState => {
    const { rulesGroups, name, description = '' } = state.conditionData;
    const {
        constants: { maxNameLength, maxDescriptionName }
    } = state;

    return {
        ...state,
        isNamePristine: false,
        conditionData: {
            ...state.conditionData,
            rulesGroups: map(rulesGroups, rulesGroup => {
                return {
                    ...rulesGroup,
                    items: map(rulesGroup.items, rule => {
                        return {
                            ...rule,
                            isIdPristine: false,
                            isPeriodPristine: false,
                            errors: {
                                id: validateRetargetingConditionRuleGoal(rule),
                                period: validateRetargetingConditionRulePeriod(
                                    { periodMaxValue: PERIOD_MAX_VALUE },
                                    rule
                                )
                            }
                        };
                    })
                };
            })
        },
        errors: {
            name: validateRetargetingConditionName(
                {
                    maxLength: maxNameLength
                },
                name
            ),
            description: validateRetargetingConditionDescription(
                {
                    maxLength: maxDescriptionName
                },
                description
            ),
            general: validateConditionsGroups(rulesGroups),
            goalsOrSegment: getSegmentOrGoalError(state.conditionData.rulesGroups)
        }
    };
};

const validateAudienceRetargetingConditionEditorData = (
    state: RetargetingConditionEditorState
): RetargetingConditionEditorState => {
    const { rulesGroups } = state.conditionData;

    return {
        ...state,
        isNamePristine: false,
        conditionData: {
            ...state.conditionData,
            rulesGroups: map(rulesGroups, rulesGroup => {
                return {
                    ...rulesGroup,
                    items: map(rulesGroup.items, rule => {
                        return {
                            ...rule,
                            isIdPristine: false,
                            isPeriodPristine: false,
                            errors: {
                                id: validateRetargetingConditionRuleGoal(rule),
                                period: validateRetargetingConditionRulePeriod(
                                    { periodMaxValue: PERIOD_MAX_VALUE },
                                    rule
                                )
                            }
                        };
                    })
                };
            })
        },
        errors: {
            goalsOrSegment: getSegmentOrGoalError(state.conditionData.rulesGroups)
        }
    };
};
